import { getCaptureClockMetadata } from './captureClock';
import { saveQuickCheckInSourceNote } from './quickCheckInSourceNote';
import { QUICK_LOG_SMART_CAPTURE_ACTIONS } from './quickLogSmartCaptureFlow';
import { getCalendarDateKey } from './calendarDateKey';

const toTrimmedString = (value) => String(value || '').trim();
const toSourceString = (value) => String(value || '');

const serializeDateForCaptureSource = (value) => {
  if (value instanceof Date) {
    return value.toISOString();
  }

  return value || null;
};

export const buildQuickCheckInCaptureSource = ({
  text = '',
  sourceText: sourceTextInput = text,
  normalizedText = '',
  sourceType = 'quick_check_in',
  sourceMode = '',
  inputMode = '',
  child = null,
  selectedDate = null,
  selectedTime = '',
  importantMoment = false,
  routingDecision = {},
  routingPreview = {},
  transcriptNormalization = {},
  structureDecision = {},
  quickNoteCategoryDecision = null,
  metadata = {},
} = {}) => {
  const sourceText = toSourceString(sourceTextInput);

  return {
    sourceType,
    sourceMode,
    inputMode,
    sourceText,
    originalText: sourceText,
    rawText: sourceText,
    normalizedText: toTrimmedString(normalizedText),
    childId: child?.id || null,
    childName: child?.name || null,
    ...getCaptureClockMetadata(selectedDate),
    selectedDate: serializeDateForCaptureSource(selectedDate),
    selectedLocalDate: getCalendarDateKey(selectedDate),
    selectedTime: toTrimmedString(selectedTime),
    importantMoment: Boolean(importantMoment),
    routingMode: routingDecision?.mode || '',
    routingReason: routingDecision?.reason || '',
    metadata: {
      ...metadata,
      routingDecision,
      routingPreview,
      transcriptNormalization,
      structureDecision,
      quickNoteCategoryDecision,
    },
  };
};

const attachSourceNoteToCaptureSource = (captureSource = {}, sourceNote = null) => {
  const sourceNoteId = sourceNote?.id || sourceNote?.sourceNoteId || '';
  if (!sourceNoteId) {
    return captureSource;
  }

  return {
    ...captureSource,
    sourceNoteId,
    sourceNotePath: sourceNote.path || sourceNote.sourceNotePath || '',
    sourceNoteCollection: sourceNote.collection || sourceNote.sourceNoteCollection || '',
    sourceNoteType: sourceNote.type || sourceNote.sourceNoteType || '',
  };
};

export const attachQuickCheckInCaptureSourceToEntry = (entry = {}, captureSource = {}) => {
  if (!entry || typeof entry !== 'object') {
    return entry;
  }

  const sourceText = toSourceString(captureSource.sourceText || captureSource.originalText);
  const entryMetadata = entry.metadata && typeof entry.metadata === 'object' ? entry.metadata : {};

  return {
    ...entry,
    sourceNoteId: entry.sourceNoteId || captureSource.sourceNoteId || null,
    sourceNotePath: entry.sourceNotePath || captureSource.sourceNotePath || '',
    sourceNoteCollection: entry.sourceNoteCollection || captureSource.sourceNoteCollection || '',
    sourceNoteType: entry.sourceNoteType || captureSource.sourceNoteType || '',
    sourceText: entry.sourceText || sourceText,
    originalText: entry.originalText || sourceText,
    rawText: entry.rawText || sourceText,
    rawSourceText: entry.rawSourceText || sourceText,
    metadata: {
      ...entryMetadata,
      originalRawText: entryMetadata.originalRawText || sourceText,
      captureFirstSourceType: captureSource.sourceType || 'quick_check_in',
      captureFirstSourcePreserved: Boolean(sourceText.trim()),
      captureFirstSelectedDate: captureSource.selectedDate || null,
      captureFirstSelectedTime: captureSource.selectedTime || '',
      sourceNoteId: entryMetadata.sourceNoteId || entry.sourceNoteId || captureSource.sourceNoteId || null,
      sourceNotePath: entryMetadata.sourceNotePath || entry.sourceNotePath || captureSource.sourceNotePath || '',
      sourceNoteCollection: entryMetadata.sourceNoteCollection || entry.sourceNoteCollection || captureSource.sourceNoteCollection || '',
      sourceNoteType: entryMetadata.sourceNoteType || entry.sourceNoteType || captureSource.sourceNoteType || '',
    },
  };
};

export const attachQuickCheckInCaptureSourceToEntries = (entries = [], captureSource = {}) => (
  Array.isArray(entries)
    ? entries.map((entry) => attachQuickCheckInCaptureSourceToEntry(entry, captureSource))
    : []
);

const attachCaptureSourceToDebugPayload = (debugPayload = null, captureSource = {}) => {
  if (!debugPayload || typeof debugPayload !== 'object') {
    return debugPayload;
  }

  return {
    ...debugPayload,
    captureFirstSourceType: captureSource.sourceType || 'quick_check_in',
    captureFirstSourcePreserved: Boolean(String(captureSource.originalText || '').trim()),
    sourceNoteId: captureSource.sourceNoteId || debugPayload.sourceNoteId || null,
    sourceNotePath: captureSource.sourceNotePath || debugPayload.sourceNotePath || '',
  };
};

const shouldRunCaptureFirstBackend = (routingDecision = {}) => (
  Boolean(routingDecision.shouldUseBackendExtraction)
  || routingDecision.mode === 'long_note_review'
  || routingDecision.baseMode === 'long_note_review'
  || routingDecision.finalMode === 'long_note_review'
);

export const buildQuickCheckInCareSourceMetadata = (captureSource = {}) => ({
  sourceKind: captureSource.sourceType === 'dictated_text' ? 'audio' : 'text',
  sourceContext: 'timeline',
  documentType: 'note',
  textExtractionStatus: 'not_needed',
  processingStatus: 'captured',
  reviewState: 'not_needed',
  sourceName: captureSource.sourceType === 'dictated_text' ? 'Voice note' : 'Quick Log note',
  inputMode: captureSource.inputMode || 'capture_first',
  selectedDate: captureSource.selectedDate || null,
  selectedLocalDate: captureSource.selectedLocalDate || null,
  selectedDateKey: captureSource.selectedLocalDate || null,
  selectedTime: captureSource.selectedTime || '',
  timeZone: captureSource.timeZone || '',
  sourceMode: captureSource.sourceMode || 'quick_log_capture_first',
  routingMode: captureSource.routingMode || '',
  routingReason: captureSource.routingReason || '',
  importantMoment: Boolean(captureSource.importantMoment),
  textLength: String(captureSource.rawText || captureSource.originalText || '').length,
  ...(Array.isArray(captureSource.metadata?.imageAttachments) ? {
    imageAttachments: captureSource.metadata.imageAttachments,
    mediaUrls: captureSource.metadata.imageAttachments
      .map((attachment) => attachment?.url)
      .filter(Boolean),
  } : {}),
  quickLogCapture: {
    routingDecision: captureSource.metadata?.routingDecision || {},
    routingPreview: captureSource.metadata?.routingPreview || {},
    transcriptNormalization: captureSource.metadata?.transcriptNormalization || {},
    structureDecision: captureSource.metadata?.structureDecision || {},
    quickNoteCategoryDecision: captureSource.metadata?.quickNoteCategoryDecision || null,
  },
});

const attachCareSourceToCaptureSource = (captureSource = {}, careSourceResult = {}) => {
  if (!careSourceResult?.sourceNoteId) {
    return captureSource;
  }

  return {
    ...captureSource,
    sourceNoteId: careSourceResult.sourceNoteId,
    sourceNotePath: `careSources/${careSourceResult.sourceNoteId}`,
    sourceNoteCollection: 'careSources',
    sourceNoteType: 'care_source',
  };
};

export const runCaptureFirstSourceProcessingFlow = async ({
  captureCareSourceForProcessing,
  persistedSourceNote = null,
  captureSource = {},
  routingDecision = {},
  routingPreview = {},
} = {}) => {
  if (
    (!persistedSourceNote && typeof captureCareSourceForProcessing !== 'function')
    || !captureSource.childId
  ) {
    return null;
  }

  const savedId = persistedSourceNote?.sourceNoteId || persistedSourceNote?.id;
  if (persistedSourceNote && (!savedId || (persistedSourceNote.childId && persistedSourceNote.childId !== captureSource.childId))) {
    throw new Error('The acknowledged capture does not match this note.');
  }
  // The source-draft saver already starts background processing. Capturing again
  // here creates another source, outside that first request's idempotency boundary.
  const captureResult = savedId ? {ok: true, sourceNoteId: savedId, status: persistedSourceNote.status || 'captured'} : await captureCareSourceForProcessing({
    childId: captureSource.childId,
    rawText: captureSource.rawText || captureSource.originalText || captureSource.sourceText || '',
    sourceType: captureSource.sourceType || 'quick_check_in',
    sourceMetadata: buildQuickCheckInCareSourceMetadata(captureSource),
  });
  const captureSourceWithCareSource = attachCareSourceToCaptureSource(captureSource, captureResult);

  if (!captureResult?.ok || !captureResult?.sourceNoteId) {
    return null;
  }

  return {
    ok: true,
    action: QUICK_LOG_SMART_CAPTURE_ACTIONS.SOURCE_PROCESSED,
    captureSource: captureSourceWithCareSource,
    sourceProcessingResult: {
      captureResult,
    },
    debugPayload: attachCaptureSourceToDebugPayload({
      captureMode: 'capture_first_source_saved',
      finalMode: 'capture_first',
      backendOk: true,
      sourceCaptured: true,
      sourceOrganized: false,
      sourceProcessingStatus: captureResult?.status || 'captured',
      savedCount: 0,
      timelineEntryIds: [],
      readyToSaveCount: 0,
      needsReviewCount: 0,
      routingPreview,
      routingReason: routingDecision.reason || '',
    }, captureSourceWithCareSource),
    error: null,
  };
};

export const attachQuickCheckInCaptureSourceToResult = (result = {}, captureSource = {}) => {
  if (!result || typeof result !== 'object') {
    return result;
  }

  const reviewPayload = result.reviewPayload && typeof result.reviewPayload === 'object'
    ? {
        ...result.reviewPayload,
        captureSource,
        sourceNote: {
          id: captureSource.sourceNoteId || null,
          path: captureSource.sourceNotePath || '',
          collection: captureSource.sourceNoteCollection || '',
          sourceType: captureSource.sourceType || 'quick_check_in',
          originalText: captureSource.originalText || '',
          rawText: captureSource.rawText || '',
        },
        structuredEntries: attachQuickCheckInCaptureSourceToEntries(
          result.reviewPayload.structuredEntries,
          captureSource
        ),
        quickLogDebugPayload: attachCaptureSourceToDebugPayload(
          result.reviewPayload.quickLogDebugPayload,
          captureSource
        ),
      }
    : result.reviewPayload;

  return {
    ...result,
    captureSource,
    debugPayload: attachCaptureSourceToDebugPayload(result.debugPayload, captureSource),
    reviewPayload,
  };
};

export const runQuickCheckInCaptureFirstFlow = async ({
  runQuickLogSmartCaptureFlow,
  saveCaptureSourceNote = saveQuickCheckInSourceNote,
  persistedSourceNote = null,
  extractQuickLogEntries = null,
  extractRawCareCaptureWithAi: _extractRawCareCaptureWithAi = null,
  captureCareSourceForProcessing = null,
  text = '',
  sourceText = text,
  normalizedText = '',
  child = null,
  selectedDate = null,
  selectedTime = '',
  importantMoment = false,
  routingDecision = {},
  routingPreview = {},
  transcriptNormalization = {},
  structureDecision = {},
  quickNoteCategoryDecision = null,
  imageAttachments = [],
  detectedQuickNoteEntities = [],
  hasReviewHandler = false,
  user = null,
  deps = {},
} = {}) => {
  if (typeof runQuickLogSmartCaptureFlow !== 'function') {
    throw new Error('runQuickLogSmartCaptureFlow dependency is required for QuickCheckIn capture-first flow');
  }

  const captureSource = buildQuickCheckInCaptureSource({
    text,
    sourceText,
    normalizedText,
    child,
    selectedDate,
    selectedTime,
    importantMoment,
    routingDecision,
    routingPreview,
    transcriptNormalization,
    structureDecision,
    quickNoteCategoryDecision,
    metadata: {
      imageAttachments: Array.isArray(imageAttachments) ? imageAttachments.filter(Boolean) : [],
    },
  });
  const shouldUseCaptureFirstBackend = shouldRunCaptureFirstBackend(routingDecision);

  if (shouldUseCaptureFirstBackend) {
    const sourceProcessingResult = await runCaptureFirstSourceProcessingFlow({
      captureCareSourceForProcessing,
      persistedSourceNote,
      captureSource,
      routingDecision,
      routingPreview,
    });

    return sourceProcessingResult || {
      ok: false,
      action: QUICK_LOG_SMART_CAPTURE_ACTIONS.SOURCE_PROCESSED,
      captureSource,
      sourceProcessingResult: {
        fallbackReason: 'source_processing_unavailable',
      },
      debugPayload: attachCaptureSourceToDebugPayload({
        captureMode: 'capture_first_source_save_failed',
        finalMode: 'capture_first',
        backendOk: false,
        sourceCaptured: false,
        sourceOrganized: false,
        savedCount: 0,
        timelineEntryIds: [],
        readyToSaveCount: 0,
        needsReviewCount: 0,
        routingPreview,
        routingReason: routingDecision.reason || '',
      }, captureSource),
      error: new Error('Source processing is unavailable.'),
    };
  }

  const savedSourceNote = persistedSourceNote || (typeof saveCaptureSourceNote === 'function'
    ? await saveCaptureSourceNote({
        captureSource,
        user,
        deps,
      })
    : null);
  const captureSourceWithNote = attachSourceNoteToCaptureSource(captureSource, savedSourceNote);

  const result = await runQuickLogSmartCaptureFlow({
    text,
    normalizedText,
    child,
    selectedDate,
    selectedTime,
    importantMoment,
    routingDecision,
    routingPreview,
    transcriptNormalization,
    structureDecision,
    quickNoteCategoryDecision,
    detectedQuickNoteEntities,
    hasReviewHandler,
    extractQuickLogEntries,
  });
  const attachedResult = attachQuickCheckInCaptureSourceToResult(result, captureSourceWithNote);

  if (shouldUseCaptureFirstBackend && attachedResult.action === QUICK_LOG_SMART_CAPTURE_ACTIONS.REVIEW) {
    return {
      ...attachedResult,
      action: QUICK_LOG_SMART_CAPTURE_ACTIONS.SOURCE_PROCESSED,
      captureSource: captureSourceWithNote,
      sourceProcessingResult: {
        sourceNoteId: captureSourceWithNote.sourceNoteId || null,
        fallbackReason: 'backend_capture_first_failed',
      },
      debugPayload: attachCaptureSourceToDebugPayload({
        ...(attachedResult.debugPayload || {}),
        captureMode: attachedResult.debugPayload?.captureMode || 'capture_first_source_saved',
        finalMode: 'capture_first',
        sourceCaptured: Boolean(captureSourceWithNote.sourceNoteId),
        sourceOrganized: false,
        captureFirstFallbackUsed: false,
        structuredEntryCount: Array.isArray(attachedResult.reviewPayload?.structuredEntries)
          ? attachedResult.reviewPayload.structuredEntries.length
          : 0,
      }, captureSourceWithNote),
    };
  }

  return attachedResult;
};
